import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { Checkbox } from '@/components/ui/misc'
import { cn } from '@/lib/utils'

export interface FacetBucket {
  id: string
  name: string
  total: number
}

export interface Facets {
  dyeSources: FacetBucket[]
  fibers: FacetBucket[]
  mordants: FacetBucket[]
  manuscripts: FacetBucket[]
  institutions: FacetBucket[]
  weaves: { value: string; total: number }[]
  kinds: { value: string; total: number }[]
}

export interface FilterState {
  dyeSource: string[]
  fiber: string[]
  mordant: string[]
  manuscript: string[]
  institution: string[]
  weave: string[]
  kind: string | null
}

export const EMPTY_FILTERS: FilterState = {
  dyeSource: [],
  fiber: [],
  mordant: [],
  manuscript: [],
  institution: [],
  weave: [],
  kind: null,
}

export const activeFilterCount = (filters: FilterState): number =>
  filters.dyeSource.length +
  filters.fiber.length +
  filters.mordant.length +
  filters.manuscript.length +
  filters.institution.length +
  filters.weave.length +
  (filters.kind ? 1 : 0)

export function FilterPanel({
  facets,
  filters,
  onChange,
}: {
  facets: Facets | undefined
  filters: FilterState
  onChange: (filters: FilterState) => void
}) {
  const toggle = (key: keyof Omit<FilterState, 'kind'>, value: string) => {
    const current = filters[key]
    onChange({
      ...filters,
      [key]: current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value],
    })
  }

  return (
    <aside className="w-56 shrink-0 space-y-1">
      <div className="flex items-baseline justify-between px-1 pb-1">
        <h2 className="font-display text-sm font-semibold text-ink">Filters</h2>
        {activeFilterCount(filters) > 0 && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_FILTERS)}
            className="text-[12px] font-semibold text-brand hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      <Group title="Record type" count={filters.kind ? 1 : 0} defaultOpen>
        {(facets?.kinds ?? []).map((bucket) => (
          <FilterRow
            key={bucket.value}
            label={bucket.value === 'reference' ? 'References' : 'Experiments'}
            total={bucket.total}
            checked={filters.kind === bucket.value}
            onToggle={() =>
              onChange({ ...filters, kind: filters.kind === bucket.value ? null : bucket.value })
            }
          />
        ))}
      </Group>

      <Group title="Dye source" count={filters.dyeSource.length} defaultOpen>
        {(facets?.dyeSources ?? []).map((bucket) => (
          <FilterRow
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.dyeSource.includes(bucket.id)}
            onToggle={() => toggle('dyeSource', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Fiber" count={filters.fiber.length}>
        {(facets?.fibers ?? []).map((bucket) => (
          <FilterRow
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.fiber.includes(bucket.id)}
            onToggle={() => toggle('fiber', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Mordant" count={filters.mordant.length}>
        {(facets?.mordants ?? []).map((bucket) => (
          <FilterRow
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.mordant.includes(bucket.id)}
            onToggle={() => toggle('mordant', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Weave" count={filters.weave.length}>
        {(facets?.weaves ?? []).map((bucket) => (
          <FilterRow
            key={bucket.value}
            label={bucket.value}
            total={bucket.total}
            checked={filters.weave.includes(bucket.value)}
            onToggle={() => toggle('weave', bucket.value)}
          />
        ))}
      </Group>

      <Group title="Manuscript" count={filters.manuscript.length}>
        {(facets?.manuscripts ?? []).map((bucket) => (
          <FilterRow
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.manuscript.includes(bucket.id)}
            onToggle={() => toggle('manuscript', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Institution" count={filters.institution.length}>
        {(facets?.institutions ?? []).map((bucket) => (
          <FilterRow
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.institution.includes(bucket.id)}
            onToggle={() => toggle('institution', bucket.id)}
          />
        ))}
      </Group>
    </aside>
  )
}

function Group({
  title,
  count,
  defaultOpen,
  children,
}: {
  title: string
  count: number
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen))

  return (
    <section className="border-b border-line last:border-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-2 px-1 py-2.5 text-left"
      >
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          {title}
          {count > 0 && (
            <span className="rounded-full bg-brand px-1.5 text-[10px] font-semibold text-white">
              {count}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn('size-4 text-ink-faint transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && <div className="max-h-60 space-y-1 overflow-y-auto pb-3 pl-1 pr-1">{children}</div>}
    </section>
  )
}

function FilterRow({
  label,
  total,
  checked,
  onToggle,
}: {
  label: string
  total: number
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 py-0.5 text-[13px] text-ink-soft hover:text-ink">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="text-[11px] text-ink-faint">{total}</span>
    </label>
  )
}
